import { defineMessages, type MessageDescriptor } from 'react-intl';
import type { FootageAiStyle, FootagePeople } from 'fym-shared/src/music24/musicVideoApi';

/** The copy of the Footage panel (design music-video b1, c1, c2, h1 to h4). */
export const footageMessages = defineMessages({
  title: { id: 'app.sml.musicVideo.footage.title', defaultMessage: 'Footage' },
  tabBank: { id: 'app.sml.musicVideo.footage.tab.bank', defaultMessage: 'Bank' },
  tabAi: { id: 'app.sml.musicVideo.footage.tab.ai', defaultMessage: 'AI' },
  tabYours: { id: 'app.sml.musicVideo.footage.tab.yours', defaultMessage: 'Yours' },
  tabUpload: { id: 'app.sml.musicVideo.footage.tab.upload', defaultMessage: 'Upload' },
  panelHint: {
    id: 'app.sml.musicVideo.footage.hint',
    defaultMessage:
      'Drag a clip onto the timeline, or onto the frame to replace the selected clip. Clips snap to the beat. A click adds the clip at the play head.',
  },
  inUse: { id: 'app.sml.musicVideo.footage.inUse', defaultMessage: 'In use · {count}×' },
  sourceYours: { id: 'app.sml.musicVideo.footage.source.yours', defaultMessage: 'Yours' },
  sourceAi: { id: 'app.sml.musicVideo.footage.source.ai', defaultMessage: 'AI · yours' },
  sourceSongVideo: { id: 'app.sml.musicVideo.footage.source.songVideo', defaultMessage: 'The music video' },
  addClip: { id: 'app.sml.musicVideo.footage.addClip', defaultMessage: 'Add {label} at the play head' },
  loadFailed: { id: 'app.sml.musicVideo.footage.loadFailed', defaultMessage: 'The clips did not load.' },
  retry: { id: 'app.sml.musicVideo.footage.retry', defaultMessage: 'Try again' },

  // Bank (b1)
  bankSearch: {
    id: 'app.sml.musicVideo.footage.bank.search',
    defaultMessage: 'Search the bank: fire, road, city at night',
  },
  chipFits: { id: 'app.sml.musicVideo.footage.bank.chipFits', defaultMessage: 'Fits {section}' },
  chipNoPeople: { id: 'app.sml.musicVideo.footage.bank.chipNoPeople', defaultMessage: 'No people' },
  chipNight: { id: 'app.sml.musicVideo.footage.bank.chipNight', defaultMessage: 'Night' },
  chipNature: { id: 'app.sml.musicVideo.footage.bank.chipNature', defaultMessage: 'Nature' },
  chipChill: { id: 'app.sml.musicVideo.footage.bank.chipChill', defaultMessage: 'Calm' },
  chipEuphoric: { id: 'app.sml.musicVideo.footage.bank.chipEuphoric', defaultMessage: 'Euphoric' },
  forSection: { id: 'app.sml.musicVideo.footage.bank.forSection', defaultMessage: 'For {section}' },
  forSectionFacts: { id: 'app.sml.musicVideo.footage.bank.forSectionFacts', defaultMessage: '{from}–{to}' },
  results: { id: 'app.sml.musicVideo.footage.bank.results', defaultMessage: 'Clips of the bank' },
  bankEmpty: { id: 'app.sml.musicVideo.footage.bank.empty', defaultMessage: 'No clip fits this search.' },
  bankMore: { id: 'app.sml.musicVideo.footage.bank.more', defaultMessage: 'Show more' },
  yoursHeading: { id: 'app.sml.musicVideo.footage.bank.yoursHeading', defaultMessage: 'Yours' },
  yoursFacts: {
    id: 'app.sml.musicVideo.footage.bank.yoursFacts',
    defaultMessage: '{count, plural, one {# clip} other {# clips}}',
  },
  all: { id: 'app.sml.musicVideo.footage.bank.all', defaultMessage: 'All' },

  // Yours (c1)
  songVideoHeading: { id: 'app.sml.musicVideo.footage.yours.songVideo', defaultMessage: 'The music video' },
  songVideoFacts: { id: 'app.sml.musicVideo.footage.yours.songVideoFacts', defaultMessage: 'your file · {length}' },
  songVideoHint: {
    id: 'app.sml.musicVideo.footage.yours.songVideoHint',
    defaultMessage: 'Use any part of it as a clip. Its sound is not used.',
  },
  thisSong: { id: 'app.sml.musicVideo.footage.yours.thisSong', defaultMessage: 'Clips of this song' },
  thisSongFacts: {
    id: 'app.sml.musicVideo.footage.yours.thisSongFacts',
    defaultMessage: '{uploads, plural, one {# upload} other {# uploads}} · {ai} AI',
  },
  otherSongs: { id: 'app.sml.musicVideo.footage.yours.otherSongs', defaultMessage: 'From your other songs' },
  showMore: { id: 'app.sml.musicVideo.footage.yours.showMore', defaultMessage: 'Show {count}' },
  yoursEmpty: {
    id: 'app.sml.musicVideo.footage.yours.empty',
    defaultMessage: 'No clips yet. Upload your own footage or make an AI clip.',
  },
  yoursHint: {
    id: 'app.sml.musicVideo.footage.yours.hint',
    defaultMessage: 'Your clips belong to the song in the Catalog, like the master. The Studio uses them too.',
  },
  working: { id: 'app.sml.musicVideo.footage.yours.working', defaultMessage: '{label} · getting ready' },

  // Upload (c2)
  dropTitle: { id: 'app.sml.musicVideo.footage.upload.dropTitle', defaultMessage: 'Drop video clips here' },
  dropHint: {
    id: 'app.sml.musicVideo.footage.upload.dropHint',
    defaultMessage: 'MP4 or MOV, up to {limit}. Phone clips are fine, also vertical ones.',
  },
  choose: { id: 'app.sml.musicVideo.footage.upload.choose', defaultMessage: 'Choose files' },
  progress: { id: 'app.sml.musicVideo.footage.upload.progress', defaultMessage: '{sent} of {total}' },
  checking: { id: 'app.sml.musicVideo.footage.upload.checking', defaultMessage: 'Checking the clip' },
  stop: { id: 'app.sml.musicVideo.footage.upload.stop', defaultMessage: 'Stop' },
  keepEditing: {
    id: 'app.sml.musicVideo.footage.upload.keepEditing',
    defaultMessage: 'You can keep editing. The clip shows in Yours when it is ready.',
  },
  errType: {
    id: 'app.sml.musicVideo.footage.upload.errType',
    defaultMessage: '<b>{name}</b> is not a video we can read. Save it as MP4 or MOV and try again.',
  },
  errTooLarge: {
    id: 'app.sml.musicVideo.footage.upload.errTooLarge',
    defaultMessage: '<b>{name}</b> is over {limit}.',
  },
  errQuota: {
    id: 'app.sml.musicVideo.footage.upload.errQuota',
    defaultMessage: 'There is no room for <b>{name}</b>. Your clip storage is full.',
  },
  errQuotaFacts: {
    id: 'app.sml.musicVideo.footage.upload.errQuotaFacts',
    defaultMessage: 'There is no room for <b>{name}</b>. Your clips use {used} of {limit}.',
  },
  errNetwork: {
    id: 'app.sml.musicVideo.footage.upload.errNetwork',
    defaultMessage: 'The upload of <b>{name}</b> stopped. Check the connection and try again.',
  },
  errFailed: {
    id: 'app.sml.musicVideo.footage.upload.errFailed',
    defaultMessage: 'The upload of <b>{name}</b> failed. Try again.',
  },
  rights: {
    id: 'app.sml.musicVideo.footage.upload.rights',
    defaultMessage: 'Only upload footage you own or have the rights to. Its sound is never used.',
  },

  // AI (h1, h2)
  aiHeading: { id: 'app.sml.musicVideo.footage.ai.heading', defaultMessage: 'New clip for {section}' },
  aiHeadingSong: { id: 'app.sml.musicVideo.footage.ai.headingSong', defaultMessage: 'New clip for {title}' },
  aiQuestion: { id: 'app.sml.musicVideo.footage.ai.question', defaultMessage: 'What should the clip show?' },
  aiDefaultPrompt: {
    id: 'app.sml.musicVideo.footage.ai.defaultPrompt',
    defaultMessage: 'A {mood} shot for the {section} of the song “{title}”. Slow camera move. No text, no logos.',
  },
  aiDefaultPromptSong: {
    id: 'app.sml.musicVideo.footage.ai.defaultPromptSong',
    defaultMessage: 'A shot for the song “{title}”. Slow camera move. No text, no logos.',
  },
  aiPeople: { id: 'app.sml.musicVideo.footage.ai.people', defaultMessage: 'People' },
  aiSpec: { id: 'app.sml.musicVideo.footage.ai.spec', defaultMessage: '{seconds} s · 1080p · 16:9' },
  aiCost: {
    id: 'app.sml.musicVideo.footage.ai.cost',
    defaultMessage: '{credits, plural, one {# credit} other {# credits}}',
  },
  aiHave: { id: 'app.sml.musicVideo.footage.ai.have', defaultMessage: 'You have' },
  aiHaveNoLimit: { id: 'app.sml.musicVideo.footage.ai.haveNoLimit', defaultMessage: 'no limit' },
  aiMake: {
    id: 'app.sml.musicVideo.footage.ai.make',
    defaultMessage: 'Make the clip · {credits, plural, one {# credit} other {# credits}}',
  },
  aiYours: { id: 'app.sml.musicVideo.footage.ai.yours', defaultMessage: 'Your AI clips' },
  aiYoursFacts: { id: 'app.sml.musicVideo.footage.ai.yoursFacts', defaultMessage: '{count} · on this song' },
  aiHint: {
    id: 'app.sml.musicVideo.footage.ai.hint',
    defaultMessage: 'The text is written from the song and the section. Change it, or keep it and make.',
  },
  aiWorkingNote: {
    id: 'app.sml.musicVideo.footage.ai.workingNote',
    defaultMessage: 'You can keep editing. Each clip goes to Yours when it is ready.',
  },
  aiMaking: { id: 'app.sml.musicVideo.footage.ai.making', defaultMessage: 'Making · about 1 min' },
  aiReady: { id: 'app.sml.musicVideo.footage.ai.ready', defaultMessage: 'Ready. Drag it onto the timeline.' },
  aiRefused: {
    id: 'app.sml.musicVideo.footage.ai.refused',
    defaultMessage: 'The AI refused this text. Your {credits} credits are back. Try it with no person in it.',
  },
  aiQualityFailed: {
    id: 'app.sml.musicVideo.footage.ai.qualityFailed',
    defaultMessage: 'The clip failed our quality check. Your {credits} credits are back.',
  },
  aiBudgetFailed: {
    id: 'app.sml.musicVideo.footage.ai.budgetFailed',
    defaultMessage: 'AI is busy this month. Your {credits} credits are back.',
  },
  aiProviderQuotaFailed: {
    id: 'app.sml.musicVideo.footage.ai.providerQuotaFailed',
    defaultMessage: 'AI video is not available now. Your {credits} credits are back. Try again later.',
  },
  aiFailed: {
    id: 'app.sml.musicVideo.footage.ai.failed',
    defaultMessage: 'The clip failed. Your {credits} credits are back.',
  },
  aiLimitHint: {
    id: 'app.sml.musicVideo.footage.ai.limitHint',
    defaultMessage: 'Up to 3 clips at a time. A clip that fails or is refused costs nothing.',
  },
  aiErrorBusy: {
    id: 'app.sml.musicVideo.footage.ai.error.busy',
    defaultMessage: 'Up to 3 clips are made at a time. Wait for one to finish.',
  },
  aiErrorLater: { id: 'app.sml.musicVideo.footage.ai.error.later', defaultMessage: 'AI is busy now. Try again later.' },
  aiErrorDemo: { id: 'app.sml.musicVideo.footage.ai.error.demo', defaultMessage: 'The example artist is read only.' },
  aiErrorFailed: {
    id: 'app.sml.musicVideo.footage.ai.error.failed',
    defaultMessage: 'The clip did not start. Try again.',
  },

  aiErrorReferences: {
    id: 'app.sml.musicVideo.footage.ai.error.references',
    defaultMessage: 'Use at most 3 pictures from the cast of your directed videos.',
  },

  // A new shot with references (directed video i1 to i3)
  aiShotHeading: { id: 'app.sml.musicVideo.footage.ai.shot.heading', defaultMessage: 'New shot for {section}' },
  aiShotHeadingSong: { id: 'app.sml.musicVideo.footage.ai.shot.headingSong', defaultMessage: 'New shot for {title}' },
  aiShotQuestion: { id: 'app.sml.musicVideo.footage.ai.shot.question', defaultMessage: 'What should the shot show?' },
  aiShotMake: {
    id: 'app.sml.musicVideo.footage.ai.shot.make',
    defaultMessage: 'Make the shot · {credits, plural, one {# credit} other {# credits}}',
  },
  aiSamePrice: {
    id: 'app.sml.musicVideo.footage.ai.samePrice',
    defaultMessage: 'The same price with or without references.',
  },
  references: { id: 'app.sml.musicVideo.footage.ai.references', defaultMessage: 'References' },
  referencesCount: { id: 'app.sml.musicVideo.footage.ai.references.count', defaultMessage: '{count} of {max}' },
  referencesOptional: { id: 'app.sml.musicVideo.footage.ai.references.optional', defaultMessage: 'optional' },
  referencesHintDirected: {
    id: 'app.sml.musicVideo.footage.ai.references.hintDirected',
    defaultMessage: 'From the cast of this video. They keep faces and things the same.',
  },
  referencesHintOther: {
    id: 'app.sml.musicVideo.footage.ai.references.hintOther',
    defaultMessage: 'Use people and things from your directed videos, so they look the same here.',
  },
  referencesAdd: { id: 'app.sml.musicVideo.footage.ai.references.add', defaultMessage: 'Pick references' },
  referencesRemove: { id: 'app.sml.musicVideo.footage.ai.references.remove', defaultMessage: 'Remove {label}' },
  styleSwitch: {
    id: 'app.sml.musicVideo.footage.ai.style.switch',
    defaultMessage: 'Film it in the style of this video',
  },
  styleText: { id: 'app.sml.musicVideo.footage.ai.style.text', defaultMessage: 'Style text · optional' },
  styleTextPlaceholder: {
    id: 'app.sml.musicVideo.footage.ai.style.placeholder',
    defaultMessage: 'For example: hand-held, warm greens, film grain',
  },
  pickerTitle: {
    id: 'app.sml.musicVideo.footage.ai.picker.title',
    defaultMessage: 'Pick up to {max} references',
  },
  pickerSource: { id: 'app.sml.musicVideo.footage.ai.picker.source', defaultMessage: '{title} · directed video' },
  pickerHint: {
    id: 'app.sml.musicVideo.footage.ai.picker.hint',
    defaultMessage: 'Remove one to pick another. Each photo is shown once, also when two videos use it.',
  },
  pickerClear: { id: 'app.sml.musicVideo.footage.ai.picker.clear', defaultMessage: 'Clear' },
  pickerDone: { id: 'app.sml.musicVideo.footage.ai.picker.done', defaultMessage: 'Done' },

  // Free and trial (h4)
  lockTitle: { id: 'app.sml.musicVideo.footage.lock.title', defaultMessage: 'Make clips that nobody else has' },
  lockText: {
    id: 'app.sml.musicVideo.footage.lock.text',
    defaultMessage:
      'Describe a shot and get an 8-second clip made for this song. AI clips are on the Plus plan and up, for {credits} credits each.',
  },
  lockButton: { id: 'app.sml.musicVideo.footage.lock.button', defaultMessage: 'See the plans' },
  lockHint: {
    id: 'app.sml.musicVideo.footage.lock.hint',
    defaultMessage: 'On Free and in the trial, the editor works with the clip bank, your clips and uploads.',
  },
});

/** The look of an AI clip (h1). */
export const aiStyleMessages: Readonly<Record<FootageAiStyle, MessageDescriptor>> = defineMessages({
  film: { id: 'app.sml.musicVideo.footage.ai.style.film', defaultMessage: 'Film' },
  photo: { id: 'app.sml.musicVideo.footage.ai.style.photo', defaultMessage: 'Photo-real' },
  abstract: { id: 'app.sml.musicVideo.footage.ai.style.abstract', defaultMessage: 'Abstract' },
  animated: { id: 'app.sml.musicVideo.footage.ai.style.animated', defaultMessage: 'Animated' },
});

/** The people of an AI clip (h1). */
export const aiPeopleMessages: Readonly<Record<FootagePeople, MessageDescriptor>> = defineMessages({
  none: { id: 'app.sml.musicVideo.footage.ai.people.none', defaultMessage: 'None' },
  crowd: { id: 'app.sml.musicVideo.footage.ai.people.crowd', defaultMessage: 'Crowd' },
  person: { id: 'app.sml.musicVideo.footage.ai.people.person', defaultMessage: 'A person' },
});
